import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../utils/config';
import { useAuth } from '../contexts/AuthContext';
import './AddressPicker.css';

interface ReverseGeocodeResult {
  formatted_address: string;
  components?: {
    street?: string | null;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    postal_code?: string | null;
  };
  location?: { lat?: number; lng?: number };
}

interface AddressPickerProps {
  /** Called with the selected formatted address and its coordinates. */
  onSelect: (address: string, lat: number, lng: number) => void;
  /** Optional address to center the map on when opened. */
  initialAddress?: string;
}

type Phase = 'idle' | 'gps-working' | 'map-loading' | 'map-ready' | 'map-looking-up';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script';

const loadGoogleMapsScript = (apiKey: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if ((window as any).google?.maps) resolve();
      else existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

const AddressPicker: React.FC<AddressPickerProps> = ({
  onSelect,
  initialAddress,
}) => {
  const { getAuthHeaders } = useAuth();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [preview, setPreview] = useState<ReverseGeocodeResult | null>(null);

  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapObjRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const reverseGeocode = async (lat: number, lng: number): Promise<ReverseGeocodeResult> => {
    const resp = await fetch(`${API_URL}/api/address/reverse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ lat, lng }),
    });
    if (!resp.ok) {
      let msg = 'Could not look up address';
      try { msg = (await resp.json()).error || msg; } catch { /* non-JSON */ }
      throw new Error(msg);
    }
    return resp.json();
  };

  // Reverse-geocode a coordinate and surface it in the footer preview.
  // Component-scope so GPS, search, map click and marker drag all share it.
  const lookupAt = async (lat: number, lng: number) => {
    setPhase('map-looking-up');
    setPreview(null);
    setError(null);
    try {
      const r = await reverseGeocode(lat, lng);
      setPreview(r);
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error ? e.message : 'No address at that location');
    } finally {
      setPhase('map-ready');
    }
  };

  // ---------- GPS flow ----------
  // "My location" simply centres the map on the user's position and drops the
  // marker there. The address lookup happens through the same path as a map
  // click (lookupAt), so GPS itself never calls the backend directly.
  const handleUseMyLocation = () => {
    setError(null);
    if (!('geolocation' in navigator)) {
      setError('Geolocation is not supported by this browser.');
      return;
    }
    setPhase('gps-working');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const map = mapObjRef.current;
        if (map) {
          map.setCenter({ lat: latitude, lng: longitude });
          map.setZoom(17);
          const google = (window as any).google;
          if (google) {
            if (markerRef.current) markerRef.current.setMap(null);
            markerRef.current = new google.maps.Marker({
              position: { lat: latitude, lng: longitude },
              map,
              draggable: true,
            });
            markerRef.current.addListener('dragend', () => {
              const p = markerRef.current.getPosition();
              lookupAt(p.lat(), p.lng());
            });
          }
        }
        lookupAt(latitude, longitude);
        setPhase('map-ready');
      },
      (geoErr) => {
        setPhase('map-ready');
        setError(
          geoErr.code === geoErr.PERMISSION_DENIED
            ? 'Location permission denied — click the map or search instead.'
            : 'Could not get your location — click the map or search instead.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  // ---------- Map flow ----------
  const openMap = () => {
    setError(null);
    setPreview(null);
    setMapOpen(true);
    setPhase('map-loading');
  };

  const closeMap = () => {
    setMapOpen(false);
    setPreview(null);
    setPhase('idle');
    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
    mapObjRef.current = null;
  };

  // Initialise the map only after the modal has painted and the div is in the DOM.
  useEffect(() => {
    if (!mapOpen) return;
    if (!mapDivRef.current) return;

    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
    if (!apiKey) {
      setError('Missing REACT_APP_GOOGLE_PLACES_API_KEY — cannot load map.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await loadGoogleMapsScript(apiKey);
        if (cancelled || !mapDivRef.current) return;

        const google = (window as any).google;
        const startPos = { lat: 31.9539, lng: 35.9106 }; // Amman default

        const map = new google.maps.Map(mapDivRef.current, {
          center: startPos,
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        mapObjRef.current = map;
        setPhase('map-ready');

        const placeMarker = (lat: number, lng: number) => {
          if (markerRef.current) markerRef.current.setMap(null);
          markerRef.current = new google.maps.Marker({
            position: { lat, lng },
            map,
            draggable: true,
          });
          markerRef.current.addListener('dragend', () => {
            const p = markerRef.current.getPosition();
            lookupAt(p.lat(), p.lng());
          });
        };

        // In-modal search box (Places SearchBox)
        if (searchInputRef.current && google.maps.places) {
          const searchBox = new google.maps.places.SearchBox(searchInputRef.current);
          map.addListener('bounds_changed', () => searchBox.setBounds(map.getBounds()));
          searchBox.addListener('places_changed', () => {
            const places = searchBox.getPlaces();
            const place = places && places[0];
            if (!place?.geometry?.location) return;
            const loc = place.geometry.location;
            map.setCenter(loc);
            map.setZoom(17);
            placeMarker(loc.lat(), loc.lng());
            lookupAt(loc.lat(), loc.lng());
          });
        }

        // Centre on saved/current address if we have one
        if (initialAddress) {
          new google.maps.Geocoder().geocode(
            { address: initialAddress },
            (results: any, status: string) => {
              if (status === 'OK' && results?.[0]?.geometry?.location) {
                map.setCenter(results[0].geometry.location);
                map.setZoom(16);
              }
            },
          );
        }

        map.addListener('click', (e: any) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          placeMarker(lat, lng);
          lookupAt(lat, lng);
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Map failed to load');
          setPhase('idle');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [mapOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmSelection = () => {
    if (!preview?.formatted_address) return;
    const { lat, lng } = preview.location ?? {};
    onSelect(
      preview.formatted_address,
      lat ?? mapObjRef.current?.getCenter()?.lat() ?? 0,
      lng ?? mapObjRef.current?.getCenter()?.lng() ?? 0,
    );
    closeMap();
  };

  // ---------- render ----------

  // The overlay must escape any transformed ancestor (the inline host uses
  // transform, which would otherwise become the containing block for
  // position: fixed) — so the modal renders through a portal to <body>.
  const mapModal = mapOpen && createPortal(
    <div className="ap2-overlay" onClick={closeMap}>
      <div className="ap2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ap2-head">
          <h3 className="ap2-title">Pick a location</h3>
          <button type="button" className="ap2-close" onClick={closeMap} aria-label="Close">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="ap2-body">
          <div className="ap2-toolbar">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for a place or address…"
              className="ap2-search-input"
            />
            <button
              type="button"
              className="ap2-btn-secondary ap2-gps-btn"
              onClick={handleUseMyLocation}
              disabled={phase === 'gps-working'}
              title="Use my current GPS location"
            >
              {phase === 'gps-working' ? 'Locating…' : 'My location'}
            </button>
          </div>
          <div ref={mapDivRef} className="ap2-map" />
          <div className="ap2-hint">
            {phase === 'gps-working' && 'Getting your location…'}
            {phase === 'map-looking-up' && 'Looking up address…'}
            {phase === 'map-loading' && 'Loading map…'}
            {phase === 'map-ready' && !preview && 'Search, use my location, or click the map — then press Apply.'}
            {phase === 'map-ready' && preview && `Selected: ${preview.formatted_address}`}
          </div>
          {error && <div className="ap2-error ap2-error-block">{error}</div>}
        </div>
        <div className="ap2-foot">
          <button type="button" className="ap2-btn-secondary" onClick={closeMap}>
            Cancel
          </button>
          <button
            type="button"
            className="ap2-btn-primary"
            onClick={confirmSelection}
            disabled={!preview?.formatted_address}
          >
            Apply address
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

  // Single map chip pinned inside the input's right edge. GPS, search and
  // click-to-pick all live inside the modal now.
  return (
    <div className="ap2-inline-host">
      <button
        type="button"
        className="ap2-icon"
        onClick={openMap}
        title="Pick a location — search, GPS, or click the map"
        aria-label="Pick a location on the map"
      >
        <i className="fas fa-map-marker-alt" aria-hidden="true" />
      </button>
      {mapModal}
    </div>
  );
};

export default AddressPicker;
